import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../../api/axios";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  ContentCopy as CloneIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import styles from "./GetEvents.module.css";

const GetEvents = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [coordinators, setCoordinators] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0, limit: 25 });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [sortModel, setSortModel] = useState([{ field: "EventsID", sort: "desc" }]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const fetchData = useCallback(
  async ({
    silent = false,
    search = searchTerm,
    page = paginationModel.page,
    pageSize = paginationModel.pageSize,
    sort = sortModel,
  } = {}) => {
    setIsLoading(true);
    setError(false);
    try {
      const activeSort = sort?.[0];
      const resp = await axios.get("/api/events/withCounts", {
        params: {
          page: page + 1,
          limit: pageSize,
          search: search.trim() || undefined,
          sortField: activeSort?.field,
          sortOrder: activeSort?.sort,
        }
      });

      const coordinatorResp = await axios.get('/api/users/role/18');
      setData(resp.data?.events || []);
      setPagination(resp.data?.pagination || { currentPage: 1, totalPages: 1, totalCount: 0, limit: 25 });
      setCoordinators(coordinatorResp.data);
      if (!silent) enqueueSnackbar("Events loaded", { variant: "success" });
    } catch (e) {
      setError(true);
      if (!silent) enqueueSnackbar(e?.response?.data?.error || "Event fetch failed", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  },
  [enqueueSnackbar, paginationModel.page, paginationModel.pageSize, searchTerm, sortModel]
);

useEffect(() => {
  fetchData();
}, [fetchData]);

const handleSortModelChange = (newSortModel) => {
  const effectiveModel = newSortModel.length ? newSortModel : [{ field: "EventsID", sort: "desc" }];
  setSortModel(effectiveModel);
  setPaginationModel((prev) => ({ ...prev, page: 0 }));
};

const handleSearch = () => {
  setSearchTerm(searchInput);
  setPaginationModel((prev) => ({ ...prev, page: 0 }));
};

const handleClearSearch = () => {
  setSearchInput('');
  setSearchTerm('');
  setPaginationModel((prev) => ({ ...prev, page: 0 }));
};

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined,{zone: "utc"});
  };

 const coordinatorLabel = (row) => {
  if (row?.BP_Coordinator != null && parseInt(row?.BP_Coordinator) > 0) {
    const coordinator = coordinators.find(v => v.UserID === parseInt(row.BP_Coordinator));
    //console.log('Coordinator Info:',coordinator);
    return coordinator
      ? `${coordinator.FirstName} ${coordinator.LastName}`
      : row?.BP_ContactName || row?.BP_ContactEmail || "";
  } else {
    return row?.BP_ContactName || row?.BP_ContactEmail || "";
  }
};

  const openDelete = (row) => setDeleteDialog({ open: true, event: row });
  const closeDelete = () => {
    if (isDeleting) return;
    setDeleteDialog({ open: false, event: null });
  };

  const confirmDelete = async () => {
    const id = deleteDialog?.event?.EventsID;
    if (!id) return;
    setIsDeleting(true);
    try {
      await axios.delete(`/api/events/${id}`);
      enqueueSnackbar("Event deleted", { variant: "success" });
      setDeleteDialog({ open: false, event: null });
      await fetchData({ silent: true });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || "Failed to delete event", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicate = async (row) => {
    const id = row?.EventsID;
    if (!id) return;
    setIsDuplicating(true);
    try {
      const resp = await axios.post(`/api/events/${id}/duplicate`);
      const newId = resp.data?.newEventId;
      enqueueSnackbar("Event duplicated", { variant: "success" });
      if (newId) navigate(`/admin/events/${newId}`);
      else await fetchData({ silent: true });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || "Failed to duplicate event", { variant: "error" });
    } finally {
      setIsDuplicating(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Actions",
        width: 170,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        // Rendered with plain IconButtons (not type: "actions" / GridActionsCellItem).
        // The grid injects a touchRippleRef into every child of an actions column,
        // which breaks when the children are wrapped in Tooltips.
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Edit Event" arrow placement="top">
              <IconButton
                size="small"
                aria-label="Edit Event"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/events/${params.row.EventsID}`);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="View Attendees" arrow placement="top">
              <IconButton
                size="small"
                aria-label="View Attendees"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/events/attendees/${params.row.EventsID}`);
                }}
              >
                <PersonIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Duplicate Event" arrow placement="top">
              {/* span lets the tooltip work while the button is disabled */}
              <span>
                <IconButton
                  size="small"
                  aria-label="Duplicate Event"
                  disabled={isDuplicating}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(params.row);
                  }}
                >
                  <CloneIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete Event" arrow placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Delete Event"
                  disabled={isDeleting}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDelete(params.row);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )
      },
      { field: "EventsID", headerName: "ID", minWidth: 90, flex: 0.4 },
      { field: "BP_Title", headerName: "Title", minWidth: 260, flex: 1.2 },
      { field: "BP_Type", headerName: "Type", minWidth: 120, flex:1},
      {
        field: "BP_StartDate",
        headerName: "Start Date",
        minWidth: 160,
        flex: 0.7,
        valueFormatter: (p) => formatDate(p.value)
      },
      {
        field: "BP_EndDate",
        headerName: "End Date",
        minWidth: 160,
        flex: 0.7,
        valueFormatter: (p) => formatDate(p.value)
      },
      {
        field: "coordinator",
        headerName: "Coordinator",
        minWidth: 220,
        flex: 1,
        valueGetter: (p) => {
          if (!coordinators.length) return ""; // wait until coordinators are loaded
          const row = p.row;
          if (row?.BP_Coordinator != null && parseInt(row?.BP_Coordinator) > 0) {
            const coordinator = coordinators.find(v => v.UserID === parseInt(row.BP_Coordinator));
            return coordinator
              ? `${coordinator.FirstName} ${coordinator.LastName}`
              : row?.BP_ContactName || row?.BP_ContactEmail || "";
          }
          return row?.BP_ContactName || row?.BP_ContactEmail || "";
        }
      },
      {
        field: "attendeeCount",
        headerName: "Attendee Count",
        minWidth: 150,
        flex: 0.6,
        type: "number",
        sortable: false
      }
    ],
    [isDeleting, isDuplicating, navigate, coordinators]
  );

  if (isLoading) {
    return (
      <main>
        {/* Blue Header Banner */}
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Manage Events</h1>
                <p>View and manage events and attendees</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.loadingContainer}>
            <CircularProgress />
            <Typography className={styles.loadingText}>Loading events...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        {/* Blue Header Banner */}
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Manage Events</h1>
                <p>View and manage events and attendees</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.errorContainer}>
            <Typography color="error">Error occurred while fetching events, Please contact an admin</Typography>
          </Box>
        </div>
      </main>
    );
  }

  const totalCount = pagination?.totalCount || 0;
  const pageCount = pagination?.totalPages || 1;

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Manage Events</h1>
              <p>View and manage events and attendees</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
      <Box className={styles.container}>
      <Paper className={styles.searchPaper}>
        <Box className={styles.buttonContainer}>
          <Button
            variant="contained"
            onClick={() => navigate("/admin/events/create")}
            startIcon={<AddIcon />}
            className={styles.addButton}
          >
            Create New Event
          </Button>
        </Box>

        <Box className={styles.searchContainer}>
          <TextField
            className={styles.searchField}
            placeholder="Search events by title, coordinator, or date"
            value={searchInput}
            onChange={(e) => {
              const val = e.target.value;
              setSearchInput(val);
              // When the field is cleared (e.g. backspaced to empty), reload the
              // full list — same as clicking the clear (✕) button.
              if (val === '' && searchTerm !== '') {
                handleClearSearch();
              }
            }}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <ClearIcon
                    className={styles.clearIcon}
                    onClick={handleClearSearch}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={isMobile ? undefined : <SearchIcon />}
            onClick={handleSearch}
            className={`${styles.searchButton} ${styles.iconSearchButton}`}
            aria-label="Search"
          >
            {isMobile ? <SearchIcon /> : "Search"}
          </Button>
        </Box>
      </Paper>

      <Paper className={styles.dataGridPaper}>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.EventsID}
          autoHeight
          disableRowSelectionOnClick
          hideFooterPagination
          sortingMode="server"
          sortModel={sortModel}
          sortingOrder={['asc', 'desc']}
          onSortModelChange={handleSortModelChange}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No events found</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search term.</Typography>
              </Box>
            ),
          }}
          className="eventsDataGrid"
          onRowClick={(params) => navigate(`/admin/events/${params.row.EventsID}`)}
        />
      </Paper>

      <Paper className={styles.paginationPaper}>
        <Stack
          className={styles.paginationStack}
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ width: "100%" }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {totalCount > 0 ? paginationModel.page * paginationModel.pageSize + 1 : 0} to{" "}
            {Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalCount)} of {totalCount} events
          </Typography>

          <Stack className={styles.paginationControls} direction="row" alignItems="center">
            <Pagination
              count={pageCount}
              page={paginationModel.page + 1}
              onChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}
              color="primary"
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className="eventsPagination"
            />
          </Stack>

          <Box className={styles.pageSizeSelector}>
            <Typography variant="body2" color="text.secondary">
              Show:
            </Typography>
            <TextField
              select
              size="small"
              value={paginationModel.pageSize}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value);
                setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
              }}
              SelectProps={{ native: true }}
              className={`${styles.pageSizeField} pageSizeField`}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              per page
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Dialog open={deleteDialog.open} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Delete Event</DialogTitle>
        <DialogContent>
          <Typography variant="body1" className={styles.dialogText}>
            Are you sure you want to permanently delete this event?
          </Typography>
          <Box className={styles.userInfoBox}>
            <Typography variant="body1" className={styles.userInfoName}>
              <strong>Title:</strong> {deleteDialog?.event?.BP_Title || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>ID:</strong> {deleteDialog?.event?.EventsID || "N/A"}
            </Typography>
          </Box>
          <Alert severity="error">
            <strong>Warning:</strong> This action cannot be undone. The event record will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeDelete} variant="outlined" className={styles.cancelButton} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            className={styles.confirmButtonDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress color="inherit" size={16} /> : <DeleteIcon />}
          >
            Delete Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
      </div>
    </main>
  );
};

export default GetEvents;